/**
 * @typedef {typeof import('widgets/Widget').default} Widget
 */

/**
 * @param {Widget} Widget Base widget for extending
 * @returns {typeof ListAccessibility} ListAccessibility widget
 */
export default function (Widget) {
    /**
     * @category widgets
     * @subcategory global
     * @class ListAccessibility
     * @augments Widget
     * @classdesc Represents ListAccessibility component that implement Basic Accessibility requirements for list of elements.
     * Has next features:
     * 1. Define list of focusable elements inside current widget
     * 2. Allow set focus to item or current item
     * 3. Allow set focus to first/last item
     * 4. Allow set focus to next/previous item
     *
     * Widget should be not used as standalone widget, it should be mixed to other widgets (see {@tutorial WidgetsMixinsStrategy})
     */
    class ListAccessibility extends Widget {
        prefs() {
            return {
                itemLink: 'itemLink',
                ...super.prefs()
            };
        }

        /**
         * @description Define Items
         * @param {InstanceType<Widget>} [currentItem] Current Item
         * @returns {void}
         */
        defineItems(currentItem) {
            if (this.items) {
                this.focusableItems = [];
                this.focusableItems = this.items.filter((item) => {
                    return item.isShown() && 'focus' in item;
                });

                if (this.focusableItems.includes(currentItem)) {
                    this.currentItem = currentItem;
                } else {
                    this.currentItem = this.focusableItems[0];
                }

                this.firstItem = this.focusableItems[0];
                this.lastItem = this.focusableItems[this.focusableItems.length - 1];
            }
        }

        /**
         * @description Set focus to item
         * @param {HTMLElement|undefined} item item
         * @returns {void}
         */
        setFocusToItem(item) {
            if (item) {
                item.focus();
                this.currentItem = item;
            }
        }

        /**
         * @description Set focus to current item
         * @returns {void}
         */
        setFocusToCurrentItem() {
            this.setFocusToItem(this.currentItem);
        }

        /**
         * @description Set focus to first item
         * @returns {void}
         */
        setFocusToFirstItem() {
            this.setFocusToItem(this.firstItem);
        }

        /**
         * @description Set focus to last item
         * @returns {void}
         */
        setFocusToLastItem() {
            this.setFocusToItem(this.lastItem);
        }

        /**
         * @description Get item index
         * @param {InstanceType<Widget>} item item widget
         * @returns {number} Item index
         */
        getItemIndex(item) {
            return this.focusableItems ? this.focusableItems.indexOf(item) : -1;
        }

        /**
         * @description Set focus to next item
         * @returns {void}
         */
        setFocusToNextItem() {
            if (this.focusableItems) {
                const newItem = this.currentItem === this.lastItem
                    ? this.firstItem
                    : this.focusableItems[this.getItemIndex(this.currentItem) + 1];

                this.setFocusToItem(newItem);
            }
        }

        /**
         * @description Set focus to previous item
         * @returns {void}
         */
        setFocusToPreviousItem() {
            if (this.focusableItems) {
                const newItem = this.currentItem === this.firstItem
                    ? this.lastItem
                    : this.focusableItems[this.getItemIndex(this.currentItem) - 1];

                this.setFocusToItem(newItem);
            }
        }

        /**
         * @description Set Focus
         * @returns {void}
         */
        focus() {
            this.has(this.prefs().itemLink, (itemLink) => {
                itemLink.focus();
            });
        }
    }

    return ListAccessibility;
}
